import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import './HRAttendanceReport.css';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

function formatTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString('en-PK', {
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function formatHours(minutes) {
  if (!minutes) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}

function formatJoinDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' });
}

function getInitials(name) {
  if (!name) return '?';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length - 1][0] || '')).toUpperCase();
}

function getAvatarColor(name) {
  const colors = ['#3b82f6','#10b981','#f59e0b','#8b5cf6','#ef4444','#06b6d4','#f97316'];
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h += name.charCodeAt(i);
  return colors[h % colors.length];
}

function getRowStyle(record) {
  if (record.status === 'weekly_off') return 'ar-row-weekoff';
  if (record.status === 'public_holiday') return 'ar-row-holiday';
  if (record.status === 'approved_leave' || record.status === 'casual_auto') return 'ar-row-leave';
  if (!record.clock_in) return 'ar-row-absent';
  if (record.late_minutes > 0) return 'ar-row-late';
  return 'ar-row-present';
}

// Capitalise a leave type for display: 'casual' -> 'Casual'
function capType(t) {
  if (!t) return '';
  return t.charAt(0).toUpperCase() + t.slice(1);
}

// Show the SPECIFIC leave type rather than a generic "On Leave", so HR can
// account for exactly what kind of leave each day was when doing payroll.
function getStatusBadge(record) {
  if (record.status === 'weekly_off')     return { label: '🌙 Weekly Off', cls: 'ar-badge-weekoff' };
  if (record.status === 'public_holiday') return { label: '🎉 Holiday',    cls: 'ar-badge-holiday' };
  if (record.status === 'approved_leave') {
    const t = capType(record.leave_type);
    return { label: t ? `📋 ${t} Leave` : '📋 On Leave', cls: 'ar-badge-leave' };
  }
  if (record.status === 'casual_auto')    return { label: '📋 Casual (auto)', cls: 'ar-badge-leave' };
  if (!record.clock_in)                   return { label: '✗ Absent',    cls: 'ar-badge-absent' };
  if (record.late_minutes > 0)            return { label: `⏱ Late`,      cls: 'ar-badge-late' };
  return                                         { label: '✓ Present',   cls: 'ar-badge-present' };
}

// ── Monthly 3-late/6-late policy tag ─────────────────────────────────
// Separate concept from the day's own status badge above — this marks
// the specific day a 3rd/6th late TRIGGER landed this month, from
// /salary/calculate's `late_policy_triggers`. Rendered as its own pill
// so it never reads as the unrelated per-day `half_day` status.
function latePolicyBadgeStyle(trig) {
  const isSalary = trig.absorbed_by === 'salary';
  return {
    display: 'inline-block',
    marginLeft: 6,
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    background: isSalary ? '#fee2e2' : '#e0e7ff',
    color: isSalary ? '#b91c1c' : '#4338ca',
  };
}

function latePolicyBadgeLabel(trig) {
  const dayLabel = trig.label === 'full_day' ? 'Full Day' : 'Half Day';
  return trig.absorbed_by === 'salary'
    ? `⏱ ${dayLabel} · ₨${trig.amount_pkr} Deducted`
    : `⏱ ${dayLabel} · Casual Used`;
}

export default function HRAttendanceReport() {
  const [employees, setEmployees]   = useState([]);
  const [search, setSearch]         = useState('');
  const [selected, setSelected]     = useState(null);
  const [report, setReport]         = useState(null);
  const [loading, setLoading]       = useState(false);
  const [empLoading, setEmpLoading] = useState(true);
  const [month, setMonth]           = useState(new Date().getMonth() + 1);
  const [year, setYear]             = useState(new Date().getFullYear());

  // Late-policy triggers for the selected employee/month, matched against
  // report.records[].date (both 'YYYY-MM-DD').
  const [lateTriggers, setLateTriggers] = useState([]);
  // Days the late policy charged to SALARY (not absorbed by casual) — these
  // are shown as absent in the Absent tile. Display only.
  const lateSalaryDays = lateTriggers
    .filter(t => t.absorbed_by === 'salary')
    .reduce((s, t) => s + (t.day_equivalent || 0), 0);

  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Load all employees once
  useEffect(() => {
    api.getAllEmployees().then(res => {
      setEmployees(res.employees || []);
      setEmpLoading(false);
    });
  }, []);

  // Load report when employee or month/year changes
  useEffect(() => {
    if (!selected) return;
    loadReport(selected);
  }, [selected, month, year]);

  const loadReport = async (emp) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const [res, salData] = await Promise.all([
        fetch(
          `https://fotileams.com/api/attendance/employee/${emp.id}?month=${month}&year=${year}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ).then(r => r.json().then(data => ({ ok: r.ok, data }))),
        // /salary/calculate/:employee_id is keyed by the employee_id CODE
        // (e.g. BKO-EK0112), not the numeric `id` used above for attendance.
        api.calculateSalary(emp.employee_id, month, year),
      ]);
      if (!res.ok) {
        setError(res.data.error || 'Failed to load attendance data');
        setReport(null);
        setLateTriggers([]);
      } else {
        setReport(res.data);
        setLateTriggers(salData?.late_policy_triggers || []);
      }
    } catch (err) {
      console.error(err);
      setError('Network error — please try again');
      setReport(null);
      setLateTriggers([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = employees.filter(e => {
    const q = search.toLowerCase();
    return (
      e.full_name?.toLowerCase().includes(q) ||
      e.employee_id?.toLowerCase().includes(q) ||
      e.department?.toLowerCase().includes(q)
    );
  });

  const years = [];
  for (let y = new Date().getFullYear(); y >= 2024; y--) years.push(y);

  const totalHours = report ? formatHours(report.summary?.total_minutes) : '—';
  const lb = report?.leave_balance || null;

  // date (YYYY-MM-DD) -> trigger info
  const triggerByDate = {};
  lateTriggers.forEach(t => { if (t.date) triggerByDate[t.date] = t; });

  const downloadExcelReport = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `https://fotileams.com/api/reports/excel?month=${month}&year=${year}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Failed to generate report');
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `Fotile_AMS_Report_${MONTHS[month-1]}_${year}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download report. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="ar-root">

      {/* LEFT PANEL — Employee List */}
      <div className="ar-left">
        <div className="ar-left-header">
          <div className="ar-left-title">Employees</div>
          <div className="ar-left-count">{employees.length}</div>
        </div>
        <div className="ar-report-period">
          <span className="ar-report-period-label">Report Period</span>
          <div className="ar-report-period-row">
            <select className="ar-period-select" value={month} onChange={e => setMonth(parseInt(e.target.value))}>
              {MONTHS.map((m, i) => (
                <option key={i} value={i + 1}>{m}</option>
              ))}
            </select>
            <select className="ar-period-select" value={year} onChange={e => setYear(parseInt(e.target.value))}>
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
        <button className="ar-download-btn" onClick={downloadExcelReport} disabled={downloading}>
          {downloading ? '⏳ Generating…' : `📥 Download ${MONTHS[month-1]} ${year} Report`}
        </button>
        <div className="ar-search-wrap">
          <span className="ar-search-icon">🔍</span>
          <input
            className="ar-search"
            placeholder="Search name, ID, dept…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {empLoading ? (
          <div className="ar-emp-loading">Loading…</div>
        ) : (
          <div className="ar-emp-list">
            {filtered.map((emp, i) => {
              const isActive = selected?.id === emp.id;
              const color    = getAvatarColor(emp.full_name);
              return (
                <div key={i}
                  className={`ar-emp-item ${isActive ? 'ar-emp-active' : ''}`}
                  onClick={() => setSelected(emp)}
                >
                  <div className="ar-emp-av" style={{ background: color }}>
                    {getInitials(emp.full_name)}
                  </div>
                  <div className="ar-emp-info">
                    <div className="ar-emp-name">{emp.full_name}</div>
                    <div className="ar-emp-meta">{emp.employee_id} · {emp.department || 'Unassigned'}</div>
                  </div>
                  {isActive && <span className="ar-emp-arrow">›</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* RIGHT PANEL — Report */}
      <div className="ar-right">
        {!selected ? (
          <div className="ar-empty-state">
            <div className="ar-empty-icon">👆</div>
            <div className="ar-empty-title">Select an Employee</div>
            <div className="ar-empty-sub">Click any employee on the left to view their detailed attendance report</div>
          </div>
        ) : (
          <>
            {/* Employee header */}
            <div className="ar-report-header">
              <div className="ar-report-emp-row">
                <div className="ar-report-av" style={{ background: getAvatarColor(selected.full_name) }}>
                  {getInitials(selected.full_name)}
                </div>
                <div className="ar-report-emp-info">
                  <div className="ar-report-emp-name">{selected.full_name}</div>
                  <div className="ar-report-emp-meta">
                    {selected.employee_id} · {selected.department || 'Unassigned'} · {selected.city}
                  </div>
                  {report?.employee?.joining_date && (
                    <div className="ar-report-emp-joined">
                      📅 Joined {formatJoinDate(report.employee.joining_date)}
                    </div>
                  )}
                </div>
              </div>

              {/* Month/Year selector */}
              <div className="ar-period-row">
                <div className="ar-month-pills">
                  {MONTHS.map((m, i) => (
                    <button key={i}
                      className={`ar-pill ${month === i + 1 ? 'ar-pill-active' : ''}`}
                      onClick={() => setMonth(i + 1)}>
                      {m.slice(0, 3)}
                    </button>
                  ))}
                </div>
                <div className="ar-year-pills">
                  {years.map(y => (
                    <button key={y}
                      className={`ar-pill ${year === y ? 'ar-pill-active' : ''}`}
                      onClick={() => setYear(y)}>
                      {y}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {loading ? (
              <div className="ar-loading"><div className="ar-spinner" /></div>
            ) : error ? (
              <div className="ar-error-state">
                <div className="ar-error-icon">⚠️</div>
                <div className="ar-error-title">Failed to load data</div>
                <div className="ar-error-sub">{error}</div>
                <button className="ar-retry-btn" onClick={() => loadReport(selected)}>Retry</button>
              </div>
            ) : report ? (
              <>
                {/* Summary KPI strip */}
                <div className="ar-kpi-strip">
                  <div className="ar-kpi ar-kpi-green">
                    <div className="ar-kpi-num">{report.summary?.present ?? 0}</div>
                    <div className="ar-kpi-lbl">Present</div>
                  </div>
                  <div className="ar-kpi ar-kpi-amber">
                    <div className="ar-kpi-num">{report.summary?.late ?? 0}</div>
                    <div className="ar-kpi-lbl">Late</div>
                  </div>
                  <div className="ar-kpi ar-kpi-red">
                    <div className="ar-kpi-num">{report.summary?.absent ?? 0}</div>
                    <div className="ar-kpi-lbl">Absent</div>
                  </div>
                  {/* Days the late policy charged to salary (3rd/6th/9th... late
                      that casual leave did not absorb). Display only. */}
                  <div className="ar-kpi ar-kpi-red" style={{ borderStyle: 'dashed' }}>
                    <div className="ar-kpi-num">{lateSalaryDays}</div>
                    <div className="ar-kpi-lbl">Absent (Late)</div>
                  </div>
                  {/* Leave days were previously invisible here — without this the
                      month could not be reconciled for payroll. */}
                  <div className="ar-kpi ar-kpi-blue">
                    <div className="ar-kpi-num">{report.summary?.on_leave ?? 0}</div>
                    <div className="ar-kpi-lbl">On Leave</div>
                  </div>
                  <div className="ar-kpi ar-kpi-purple">
                    <div className="ar-kpi-num">{report.summary?.holidays ?? 0}</div>
                    <div className="ar-kpi-lbl">Holidays</div>
                  </div>
                  <div className="ar-kpi ar-kpi-navy">
                    <div className="ar-kpi-num ar-kpi-num-sm">{totalHours}</div>
                    <div className="ar-kpi-lbl">Total Hours</div>
                  </div>
                </div>

                {/* Leave balance panel — remaining per type for the year */}
                {lb && (
                  <div className="ar-leave-balance">
                    <div className="ar-lb-title">Leave Balance ({year})</div>
                    <div className="ar-lb-grid">
                      <div className="ar-lb-card ar-lb-casual">
                        <div className="ar-lb-type">🌴 Casual</div>
                        <div className="ar-lb-nums">
                          <span className="ar-lb-remaining">{lb.casual.remaining}</span>
                          <span className="ar-lb-total">/ {lb.casual.total} left</span>
                        </div>
                        <div className="ar-lb-used">{lb.casual.used} used</div>
                      </div>
                      <div className="ar-lb-card ar-lb-sick">
                        <div className="ar-lb-type">🤒 Sick</div>
                        <div className="ar-lb-nums">
                          <span className="ar-lb-remaining">{lb.sick.remaining}</span>
                          <span className="ar-lb-total">/ {lb.sick.total} left</span>
                        </div>
                        <div className="ar-lb-used">{lb.sick.used} used</div>
                      </div>
                      <div className="ar-lb-card ar-lb-annual">
                        <div className="ar-lb-type">✈️ Annual</div>
                        <div className="ar-lb-nums">
                          <span className="ar-lb-remaining">{lb.annual.remaining}</span>
                          <span className="ar-lb-total">/ {lb.annual.total} left</span>
                        </div>
                        <div className="ar-lb-used">{lb.annual.used} used</div>
                      </div>
                    </div>
                  </div>
                )}

                {lateTriggers.length > 0 && (
                  <div style={{
                    margin: '0 0 12px', padding: '8px 12px', borderRadius: 8,
                    background: '#fff7ed', border: '1px solid #fed7aa',
                    fontSize: 12, color: '#9a3412',
                  }}>
                    {lateTriggers.length} late-policy half/full-day {lateTriggers.length === 1 ? 'mark' : 'marks'} this month — see tagged rows below.
                  </div>
                )}

                {/* Attendance table */}
                {report.records.length === 0 ? (
                  <div className="ar-no-records">
                    No records for {MONTHS[month - 1]} {year}
                  </div>
                ) : (
                  <div className="ar-table-wrap">
                    <table className="ar-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Day</th>
                          <th>Clock In</th>
                          <th>Clock Out</th>
                          <th>Hours</th>
                          <th>Late</th>
                          <th>Status</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.records.map((r, i) => {
                          const rowCls  = getRowStyle(r);
                          const badge   = getStatusBadge(r);
                          const dateObj = new Date(r.date);
                          const day     = dateObj.toLocaleDateString('en-PK', { weekday: 'short' });
                          const dateStr = dateObj.toLocaleDateString('en-PK', { day: 'numeric', month: 'short' });
                          const trig    = triggerByDate[r.date];
                          return (
                            <tr key={i} className={rowCls}>
                              <td className="ar-td-date">{dateStr}</td>
                              <td className="ar-td-day">{day}</td>
                              <td className="ar-td-time">{formatTime(r.clock_in)}</td>
                              <td className="ar-td-time">{r.clock_in && !r.clock_out ? <span className="ar-live">● Live</span> : formatTime(r.clock_out)}</td>
                              <td className="ar-td-hours">{formatHours(r.total_minutes)}</td>
                              <td className="ar-td-late">
                                {r.late_minutes > 0
                                  ? <span className="ar-late-pill">+{r.late_minutes}m</span>
                                  : '—'}
                              </td>
                              <td className="ar-td-status">
                                <span className={`ar-badge ${badge.cls}`}>{badge.label}</span>
                                {trig && (
                                  <span style={latePolicyBadgeStyle(trig)} title={`Late-policy trigger #${trig.trigger_no} this month`}>
                                    {latePolicyBadgeLabel(trig)}
                                  </span>
                                )}
                              </td>
                              <td className="ar-td-notes">{r.notes || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}